import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Coins, Sparkles, Zap, Loader2, RotateCcw, AlertTriangle } from "lucide-react";
import VipPurchaseNudge from "@/components/vip/VipPurchaseNudge";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { initIap, buyIap, restoreIap, getIapProduct, IAP_PRODUCT_IDS, IAP_UNAVAILABLE_MSG } from "@/lib/iap";
import { isNativeIOS } from "@/lib/nativePush";
import { CoinBurst } from "@/components/CoinBurst";
import { SubscriptionLegalLinks } from "@/components/SubscriptionLegalLinks";

const ICONS = {
  coins_500: Coins,
  coins_2000: Sparkles,
  coins_3700: Sparkles,
  coins_16500: Zap,
  coins_43000: Zap,
  coins_10000: Zap,
};

// Apple packs (iOS shell) carry no tag from the API — label them as before.
const IOS_HIGHLIGHTS = { coins_500: "Starter", coins_2000: "Most popular", coins_10000: "Best value" };
const FEATURED_TAG = "Most Popular";

export default function CoinStorePage() {
  const { user, refresh } = useAuth();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(null);
  const [stripeMode, setStripeMode] = useState(null);
  // Hard gate: inside the native iOS shell, Stripe/web checkout NEVER renders.
  const [nativeIOS] = useState(() => isNativeIOS());
  const [iapState, setIapState] = useState(() => (isNativeIOS() ? "loading" : "web"));
  const [burst, setBurst] = useState(null);

  useEffect(() => {
    api.get("/payments/packages").then(({ data }) => setPackages(data)).catch(() => {});
    if (nativeIOS) {
      let cancelled = false;
      initIap().then((ok) => {
        if (!cancelled) setIapState(ok ? "ready" : "unavailable");
      });
      const onCredited = (e) => {
        const credited = e?.detail?.credited;
        if (credited) setBurst(credited);
        toast.success("Purchase complete — coins added!");
        refresh();
        setBusy(null);
      };
      const onIapError = (e) => {
        toast.error(e?.detail?.message || "Purchase could not be verified — it will retry on next app launch.");
        setBusy(null);
      };
      window.addEventListener("iap:credited", onCredited);
      window.addEventListener("iap:error", onIapError);
      return () => {
        cancelled = true;
        window.removeEventListener("iap:credited", onCredited);
        window.removeEventListener("iap:error", onIapError);
      };
    }
    api.get("/payments/health")
      .then(({ data }) => setStripeMode(data?.stripe_mode || null))
      .catch(() => setStripeMode(null));
  }, [refresh, nativeIOS]);

  const buy = async (pkg) => {
    if (nativeIOS) {
      // Apple-only path. Never fall back to Stripe/web checkout on iOS.
      if (iapState !== "ready") {
        toast.error(IAP_UNAVAILABLE_MSG);
        return;
      }
      setBusy(pkg.lookup_key);
      try {
        await buyIap(IAP_PRODUCT_IDS[pkg.lookup_key]);
      } catch (e) {
        toast.error(e?.message || "Purchase failed.");
      } finally {
        setBusy(null);
      }
      return;
    }
    setBusy(pkg.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: pkg.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed.");
      setBusy(null);
    }
  };

  const restore = async () => {
    try {
      await restoreIap();
      toast.success("Restore requested — any past purchases will re-sync.");
      setTimeout(refresh, 2500);
    } catch (e) {
      toast.error(e?.message || "Restore failed.");
    }
  };

  const iapBlocked = nativeIOS && iapState === "unavailable";

  return (
    <div data-testid="coinstore-root" className="p-6 lg:p-8">
      <CoinBurst amount={burst} onDone={() => setBurst(null)} />
      <header className="sticky top-[calc(52px+env(safe-area-inset-top))] lg:top-0 -mx-6 lg:-mx-8 px-6 lg:px-8 py-4 glass border-b border-white/5 mb-6 z-10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Coins size={20} className="text-amber-400" />
          <h1 className="font-display text-2xl tracking-tight">Coin Store</h1>
        </div>
        {user && (
          <div className="flex items-center gap-2 text-sm text-amber-400">
            <Coins size={14} />
            {(user.coins ?? 0).toLocaleString()} balance
          </div>
        )}
      </header>

      {nativeIOS ? (
        iapBlocked ? (
          <div
            data-testid="coinstore-iap-unavailable"
            className="max-w-lg mb-8 border border-amber-400/40 bg-amber-400/10 p-4 flex items-start gap-3"
          >
            <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200 leading-relaxed">{IAP_UNAVAILABLE_MSG}</p>
          </div>
        ) : (
          <p
            data-testid="coinstore-mode-banner-ios"
            className="text-sm text-zinc-400 max-w-lg mb-8 leading-relaxed"
          >
            Coins let you tip creators, unlock exclusive drops and gift the vault.
            All purchases are final and billed through your Apple ID.
          </p>
        )
      ) : stripeMode === "test" ? (
        <p
          data-testid="coinstore-mode-banner-test"
          className="text-sm text-zinc-400 max-w-lg mb-8 leading-relaxed"
        >
          Coins let you tip creators, unlock exclusive drops and gift the vault. Test mode is on —
          use card <span className="text-white">4242 4242 4242 4242</span>, any future date, any CVC.
        </p>
      ) : (
        <p
          data-testid="coinstore-mode-banner-live"
          className="text-sm text-zinc-400 max-w-lg mb-8 leading-relaxed"
        >
          Coins let you tip creators, unlock exclusive drops and gift the vault.
          All purchases are final. Secure checkout via Stripe.
        </p>
      )}

      {user && <VipPurchaseNudge className="mb-6 max-w-xl" />}

      {!nativeIOS && packages.some((p) => Number(p.intro_bonus_coins || 0) > 0) && (
        <div className="mb-4 border border-emerald-400/30 bg-emerald-400/5 px-4 py-3 text-sm text-emerald-200 flex items-start sm:items-center gap-2" data-testid="intro-bonus-banner">
          <Sparkles size={14} className="shrink-0 mt-1 sm:mt-0" /> <span>New account: your first <b>Starter pack</b> comes with <b>15% extra coins</b> (575 for $4.99).</span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="coinpack-grid">
        {packages.map((p) => {
          const Icon = ICONS[p.lookup_key] || Coins;
          const highlight = p.tag || IOS_HIGHLIGHTS[p.lookup_key];
          const dollars = (p.amount_cents / 100).toFixed(2);
          const isFeatured = highlight === FEATURED_TAG;
          const intro = !nativeIOS && Number(p.intro_bonus_coins || 0) > 0;
          return (
            <div
              key={p.lookup_key}
              data-testid={`coinpack-${p.lookup_key}`}
              className={`card-surface p-6 flex flex-col hover-lift ${isFeatured ? "border-rose-600/50" : ""}`}
            >
              <div className="flex items-center justify-between mb-6">
                <Icon size={22} className={isFeatured ? "text-rose-500" : "text-amber-400"} />
                <div className="flex items-center gap-1.5">
                  {p.save_pct ? (
                    <span data-testid={`coinpack-${p.lookup_key}-save`} className="text-[10px] uppercase tracking-widest px-2 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
                      Save {p.save_pct}%
                    </span>
                  ) : null}
                  {highlight && (
                    <span data-testid={`coinpack-${p.lookup_key}-tag`} className={`text-[10px] uppercase tracking-widest px-2 py-1 ${
                      isFeatured ? "text-rose-500 border border-rose-600/50" : "text-zinc-500 border border-white/10"
                    }`}>
                      {highlight}
                    </span>
                  )}
                </div>
              </div>
              <div className="font-display text-5xl mb-1" data-testid={`coinpack-${p.lookup_key}-coins`}>
                {(intro ? p.total_coins : p.coins).toLocaleString()}
              </div>
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-6 flex items-center gap-2 flex-wrap">
                Panda Coins
                {intro && (
                  <span data-testid={`coinpack-${p.lookup_key}-intro`} className="inline-flex items-center gap-1 rounded-full border border-emerald-400/50 bg-emerald-400/10 px-1.5 py-0.5 text-[9px] text-emerald-300 normal-case tracking-normal">
                    <Sparkles size={9} strokeWidth={2.2} /> {p.coins.toLocaleString()} + {p.intro_bonus_coins.toLocaleString()} intro bonus
                  </span>
                )}
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-display text-3xl">
                  {nativeIOS
                    ? (getIapProduct(IAP_PRODUCT_IDS[p.lookup_key])?.pricing?.price || `$${dollars}`)
                    : `$${dollars}`}
                </span>
                {!nativeIOS && <span className="text-xs text-zinc-500">USD</span>}
              </div>
              <Button
                data-testid={`coinpack-${p.lookup_key}-buy`}
                onClick={() => buy(p)}
                disabled={busy === p.lookup_key || iapBlocked || (nativeIOS && iapState === "loading")}
                className={`rounded-none w-full py-6 ${
                  isFeatured
                    ? "btn-crimson"
                    : "bg-transparent border border-white/15 text-white hover:border-rose-600 hover:bg-white/5"
                }`}
              >
                {busy === p.lookup_key ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                {busy === p.lookup_key
                  ? (nativeIOS ? "Processing…" : "Redirecting…")
                  : iapBlocked
                    ? "Unavailable"
                    : nativeIOS && iapState === "loading"
                      ? "Loading…"
                      : "Buy"}
              </Button>
            </div>
          );
        })}
      </div>

      {nativeIOS && (
        <div className="mt-8 max-w-lg space-y-4">
          <button
            type="button"
            data-testid="iap-restore-purchases-btn"
            onClick={restore}
            className="w-full flex items-center justify-center gap-2 text-xs text-zinc-400 hover:text-white py-3 border border-white/10 rounded-none transition-colors"
          >
            <RotateCcw size={13} /> Restore purchases
          </button>
          <SubscriptionLegalLinks testid="store-footer" />
        </div>
      )}

      {nativeIOS ? (
        <div className="mt-10 border-l-2 border-rose-600/50 pl-4 py-3 text-xs text-zinc-500">
          Purchases are billed to your Apple ID and managed in your device settings.
        </div>
      ) : (
      <div className="mt-10 border-l-2 border-rose-600/50 pl-4 py-3 text-xs text-zinc-500">
        Payments are processed by Stripe. My Panda X never sees or stores your card details.
      </div>
      )}
    </div>
  );
}
